/**
 * AtlasCraftingGuideModule — the CRAFTING GUIDE window (F10, the Recipes
 * menu button, the Workbench's GUIDE button, the welcome popup): every
 * design in the game, grouped by what it is, with its full recipe — the
 * resources, components and nested components it takes — and, against
 * YOUR holdings, whether you can craft it RIGHT NOW. An SWG-style window
 * (drag / resize / roll-up / opacity), pure client-side: it reads the same
 * replicated rows the Workbench does and never decides anything.
 *
 *   READY     every slot satisfied by your stacks / component items and the
 *             design is basic or held → the Craft button opens the bench on it
 *   MISSING   the design is yours to make but something is short — the
 *             short slots are listed red, with what to gather or make first
 *   LOCKED    a complex design (requiresSchematic) you do not hold — the
 *             card says where the schematic comes from
 */
import type { App, IModule } from 'enginex';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { ASSET_STORE_BASE } from '../config.js';
import { FAMILY_BY_KEY } from '../lib/resource_catalog.js';
import {
  SCHEMATICS, SCHEMATIC_BY_KEY, requiresSchematic, schematicCategory, SHIP_SCHEMATICS, DEED_SCHEMATICS,
  type Schematic, type SchematicCategory,
} from '../lib/schematic_catalog.js';
import { makeWindow, type UiWindow } from '../lib/ui_window.js';
import { verdictOf, type Holdings } from '../lib/craft_guide.js';

const CATEGORY_ORDER: SchematicCategory[] = [
  'component', 'tool', 'consumable', 'weapon', 'vehicle', 'furniture', 'rig', 'ship component', 'droid', 'ship',
];
const CATEGORY_LABEL: Record<SchematicCategory, string> = {
  component: 'COMPONENTS — parts made from raw resources (anyone can craft these)',
  tool: 'TOOLS & GEAR',
  consumable: 'CONSUMABLES',
  weapon: 'WEAPONS — guns and blades (tier 3+ need a schematic)',
  vehicle: 'HOVERCRAFT — ground vehicles',
  furniture: 'FURNITURE — for structures you own',
  rig: 'EXTRACTION RIGS — deeds you place on a hotspot',
  'ship component': 'SHIP COMPONENTS — fitted at a station',
  droid: 'DROIDS',
  ship: 'SHIP HULLS — laid down into your hangar (schematic required)',
};

type Filter = 'all' | 'ready' | 'known' | 'locked';

export class AtlasCraftingGuideModule implements IModule {
  readonly name = 'AtlasCraftingGuide';
  private win: UiWindow | null = null;
  private body: HTMLDivElement | null = null;
  private query = '';
  private filter: Filter = 'all';
  private category: SchematicCategory | 'all' = 'all';
  private refresh = 0;
  private tablesSubbed = false;
  private focusKey: string | null = null;

  init(_app: App): void {
    void _app;
    window.addEventListener('keydown', (e) => {
      if (e.code !== 'F10' || e.ctrlKey || e.metaKey || e.altKey) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      if (atlas.loadingGate) return;
      e.preventDefault();
      this.toggle();
    });
    bus.on('atlas:open-craft-guide', ({ key }) => { this.focusKey = key ?? null; this.open(); });
    bus.on('atlas:world-cleared', () => this.close());
    (window as unknown as Record<string, unknown>).__craftGuide = {
      open: (key?: string) => { this.focusKey = key ?? null; this.open(); },
      close: () => this.close(),
      isOpen: () => this.isOpen(),
      census: () => this.census(),
      setFilter: (f: Filter) => { this.filter = f; this.render(); },
    };
  }

  isOpen(): boolean { return !!this.win && this.win.isOpen(); }
  private toggle(): void { if (this.isOpen()) this.close(); else this.open(); }
  private close(): void { window.clearInterval(this.refresh); this.refresh = 0; this.win?.setOpen(false); }

  private ensureWindow(): UiWindow {
    if (this.win) return this.win;
    const win = makeWindow({
      id: 'craft-guide', title: 'Crafting Guide', icon: 'help',
      width: 820, height: 620, minWidth: 520, minHeight: 320, x: Math.max(12, (window.innerWidth - 820) / 2 + 30), y: 72,
      onClose: () => { window.clearInterval(this.refresh); this.refresh = 0; },
    });
    win.root.id = 'craft-guide-window';
    const bar = document.createElement('div');
    bar.style.cssText = 'display:flex;gap:6px;align-items:center;flex-wrap:wrap;padding:8px 12px;border-bottom:1px solid var(--ax-line);' +
      'position:sticky;top:0;z-index:1;background:color-mix(in srgb, var(--ax-bg) 98%, transparent)';
    const search = document.createElement('input');
    search.type = 'search';
    search.placeholder = 'Search designs, resources, components…';
    search.id = 'craft-guide-search';
    search.style.cssText = 'flex:1;min-width:160px;background:var(--ax-bg2);color:var(--ax-text);border:1px solid var(--ax-line);border-radius:var(--ax-radius);padding:5px 9px;font:12px var(--ax-font)';
    search.addEventListener('input', () => { this.query = search.value.trim().toLowerCase(); this.render(); });
    bar.appendChild(search);
    const chips = document.createElement('div');
    chips.id = 'craft-guide-filters';
    chips.style.cssText = 'display:flex;gap:4px';
    for (const [f, label] of [['all', 'All'], ['ready', 'Craftable now'], ['known', 'Designs I hold'], ['locked', 'Locked']] as Array<[Filter, string]>) {
      const b = document.createElement('button');
      b.textContent = label;
      b.dataset.filter = f;
      b.style.cssText = 'cursor:pointer;background:var(--ax-bg2);color:var(--ax-soft);border:1px solid var(--ax-line);border-radius:var(--ax-radius);padding:3px 9px;font:600 10px var(--ax-mono)';
      b.addEventListener('click', () => { this.filter = f; this.render(); });
      chips.appendChild(b);
    }
    bar.appendChild(chips);
    const cat = document.createElement('select');
    cat.id = 'craft-guide-category';
    cat.style.cssText = 'background:var(--ax-bg2);color:var(--ax-text);border:1px solid var(--ax-line);border-radius:var(--ax-radius);padding:3px 6px;font:11px var(--ax-font)';
    const all = document.createElement('option');
    all.value = 'all';
    all.textContent = 'every kind';
    cat.appendChild(all);
    for (const c of CATEGORY_ORDER) {
      const o = document.createElement('option');
      o.value = c;
      o.textContent = c;
      cat.appendChild(o);
    }
    cat.addEventListener('change', () => { this.category = cat.value as SchematicCategory | 'all'; this.render(); });
    bar.appendChild(cat);
    const body = document.createElement('div');
    body.style.cssText = 'padding:10px 14px 16px;font:12px var(--ax-font);color:var(--ax-text)';
    win.body.append(bar, body);
    this.win = win;
    this.body = body;
    return win;
  }

  private open(): void {
    const win = this.ensureWindow();
    win.setOpen(true);
    if (!this.tablesSubbed && atlas.conn) try {
      this.tablesSubbed = true;
      atlas.conn.subscriptionBuilder().subscribe([
        'SELECT * FROM player_resource', 'SELECT * FROM player_item', 'SELECT * FROM player_schematic',
      ]);
    } catch { /* fine */ }
    this.render();
    window.clearInterval(this.refresh);
    this.refresh = window.setInterval(() => this.render(), 3000);
  }

  // ── holdings ──────────────────────────────────────────────────────────────
  private holdings(): Holdings {
    const h: Holdings = { stackMaxByFamily: new Map(), stackMaxByClass: new Map(), itemCountByKey: new Map(), known: new Set() };
    const conn = atlas.conn;
    if (!conn?.identity) return h;
    const me = conn.identity;
    try {
      for (const st of conn.db.playerResource.iter()) {
        if (!st.owner.isEqual(me)) continue;
        h.stackMaxByFamily.set(st.familyKey, Math.max(h.stackMaxByFamily.get(st.familyKey) ?? 0, st.qty));
        const cls = FAMILY_BY_KEY[st.familyKey]?.cls;
        if (cls) h.stackMaxByClass.set(cls, Math.max(h.stackMaxByClass.get(cls) ?? 0, st.qty));
      }
      for (const it of conn.db.playerItem.iter()) {
        if (!it.owner.isEqual(me)) continue;
        h.itemCountByKey.set(it.itemKey, (h.itemCountByKey.get(it.itemKey) ?? 0) + 1);
      }
      for (const sk of conn.db.playerSchematic.iter()) {
        if (sk.owner.isEqual(me)) h.known.add(sk.schematicKey);
      }
    } catch { /* tables not replicated yet */ }
    return h;
  }

  /** Probe surface: counts per verdict over the whole catalog. */
  census(): { total: number; ready: number; missing: number; locked: number; known: number } {
    const h = this.holdings();
    const out = { total: SCHEMATICS.length, ready: 0, missing: 0, locked: 0, known: h.known.size };
    for (const sch of SCHEMATICS) out[verdictOf(sch, h).verdict]++;
    return out;
  }

  // ── render ────────────────────────────────────────────────────────────────
  private icon(key: string, glyph: string): HTMLElement {
    const wrap = document.createElement('span');
    wrap.style.cssText = 'display:inline-flex;width:34px;height:34px;align-items:center;justify-content:center;flex:0 0 auto;' +
      'background:var(--ax-bg2);border:1px solid var(--ax-line);border-radius:var(--ax-radius);overflow:hidden;font-size:15px';
    const img = document.createElement('img');
    img.src = `${ASSET_STORE_BASE}/textures/icons/${key}.png`;
    img.style.cssText = 'width:100%;height:100%;object-fit:cover';
    img.onerror = () => { img.remove(); wrap.textContent = glyph; };
    wrap.appendChild(img);
    return wrap;
  }

  private matches(sch: Schematic, q: string): boolean {
    if (!q) return true;
    if (sch.name.toLowerCase().includes(q) || sch.key.includes(q)) return true;
    for (const sl of sch.slots) {
      const acc = sl.accepts;
      if (acc.startsWith('component:')) {
        const c = SCHEMATIC_BY_KEY[acc.slice(10)];
        if (c && (c.name.toLowerCase().includes(q) || c.key.includes(q))) return true;
      } else if (acc.startsWith('family:')) {
        const f = FAMILY_BY_KEY[acc.slice(7)];
        if (f && (f.label.toLowerCase().includes(q) || f.key.includes(q))) return true;
      } else if (acc.slice(6).includes(q)) return true;
    }
    return false;
  }

  private render(): void {
    const body = this.body;
    if (!body || !this.isOpen()) return;
    const h = this.holdings();
    // Chip highlight
    for (const b of this.win!.root.querySelectorAll<HTMLButtonElement>('#craft-guide-filters button')) {
      const on = b.dataset.filter === this.filter;
      b.style.background = on ? 'var(--ax-bg3)' : 'var(--ax-bg2)';
      b.style.color = on ? 'var(--ax-text)' : 'var(--ax-soft)';
    }
    body.replaceChildren();
    const verdicts = new Map<string, ReturnType<typeof verdictOf>>();
    for (const sch of SCHEMATICS) verdicts.set(sch.key, verdictOf(sch, h));
    const counts = { ready: 0, missing: 0, locked: 0 };
    for (const v of verdicts.values()) counts[v.verdict]++;
    const summary = document.createElement('div');
    summary.id = 'craft-guide-summary';
    summary.style.cssText = 'color:var(--ax-soft);margin-bottom:10px;line-height:1.5';
    summary.textContent = `You can craft ${counts.ready} of ${SCHEMATICS.length} designs right now · ${counts.missing} need more materials · ${counts.locked} need a schematic (${h.known.size} held). `
      + 'Basic work — every component and every item up to complexity 3 — needs no schematic. Resources come from the Survey tab (sample where you stand, or place your rig on a hotspot) and the bazaar.';
    body.appendChild(summary);

    const hint = document.createElement('div');
    hint.style.cssText = 'color:var(--ax-muted);margin-bottom:12px;font:10px var(--ax-mono)';
    hint.textContent = 'READY = craftable now · MISSING = short on materials (red slots) · LOCKED = buy the schematic from a dealer in any city or town (Workbench → Market) or reverse-engineer a finished piece (Inventory; destroys it). Complexity ≥ 3 needs a workshop: within 150 m of a deployed structure or 300 m of a town center.';
    body.appendChild(hint);

    let shown = 0;
    let focusEl: HTMLElement | null = null;
    for (const cat of CATEGORY_ORDER) {
      if (this.category !== 'all' && this.category !== cat) continue;
      const list = SCHEMATICS.filter((s) => schematicCategory(s) === cat).filter((s) => {
        const v = verdicts.get(s.key)!.verdict;
        if (this.filter === 'ready' && v !== 'ready') return false;
        if (this.filter === 'locked' && v !== 'locked') return false;
        if (this.filter === 'known' && !(requiresSchematic(s) && h.known.has(s.key))) return false;
        return this.matches(s, this.query);
      });
      if (list.length === 0) continue;
      const head = document.createElement('div');
      head.textContent = CATEGORY_LABEL[cat];
      head.style.cssText = 'font:700 10px var(--ax-mono);color:var(--ax-muted);margin:10px 0 6px';
      body.appendChild(head);
      for (const sch of list) {
        const card = this.card(sch, verdicts.get(sch.key)!, h);
        if (sch.key === this.focusKey) focusEl = card;
        body.appendChild(card);
        shown++;
      }
    }
    if (shown === 0) {
      const none = document.createElement('div');
      none.textContent = this.filter === 'ready'
        ? 'Nothing is craftable from what you hold yet — gather resources on the Survey tab (or buy stacks at the bazaar) and start with a component like a Metal Ingot.'
        : 'No design matches.';
      none.style.cssText = 'color:var(--ax-muted)';
      body.appendChild(none);
    }
    if (focusEl) {
      focusEl.style.borderColor = 'var(--ax-accent)';
      focusEl.scrollIntoView({ block: 'center' });
      this.focusKey = null;
    }
  }

  private card(sch: Schematic, v: ReturnType<typeof verdictOf>, h: Holdings): HTMLElement {
    const card = document.createElement('div');
    card.className = `craft-guide-card verdict-${v.verdict}`;
    card.dataset.key = sch.key;
    card.style.cssText = 'display:flex;gap:10px;align-items:flex-start;background:color-mix(in srgb, var(--ax-bg2) 70%, transparent);' +
      'border:1px solid var(--ax-line);border-radius:var(--ax-radius);padding:8px 10px;margin-bottom:5px';
    card.appendChild(this.icon(sch.key, sch.kind === 'component' ? '⚙' : '⚒'));
    const info = document.createElement('div');
    info.style.cssText = 'flex:1;min-width:0';
    const top = document.createElement('div');
    top.style.cssText = 'display:flex;gap:8px;align-items:center;flex-wrap:wrap';
    const name = document.createElement('b');
    name.textContent = sch.name;
    top.appendChild(name);
    const meta = document.createElement('span');
    meta.style.color = 'var(--ax-muted)';
    const out = SHIP_SCHEMATICS[sch.key] ? 'a hull in your hangar' : DEED_SCHEMATICS[sch.key] ? 'a packed deed' : sch.kind === 'component' ? 'component' : 'item';
    meta.textContent = `complexity ${sch.complexity} · ${out}${sch.complexity >= 3 ? ' · workshop' : ''}`;
    top.appendChild(meta);
    const badge = document.createElement('span');
    badge.className = 'craft-guide-badge';
    const held = requiresSchematic(sch) && h.known.has(sch.key);
    badge.textContent = v.verdict === 'ready' ? 'READY' : v.verdict === 'missing' ? 'MISSING' : 'LOCKED';
    badge.style.cssText = `font:700 9px var(--ax-mono);letter-spacing:1px;padding:2px 6px;border-radius:var(--ax-radius);` +
      (v.verdict === 'ready' ? 'background:color-mix(in srgb, var(--ax-good) 22%, var(--ax-bg2));color:var(--ax-good)' : v.verdict === 'missing' ? 'background:color-mix(in srgb, var(--ax-accent) 22%, var(--ax-bg2));color:var(--ax-hi)' : 'background:var(--ax-bg3);color:var(--ax-bad)');
    top.appendChild(badge);
    if (held) {
      const hb = document.createElement('span');
      hb.textContent = 'schematic held';
      hb.style.cssText = 'font:600 9px var(--ax-mono);color:var(--ax-text)';
      top.appendChild(hb);
    }
    info.appendChild(top);
    const slots = document.createElement('div');
    slots.style.cssText = 'margin-top:4px;display:flex;flex-direction:column;gap:2px';
    v.slots.forEach((sv, i) => {
      const line = document.createElement('div');
      line.className = 'craft-guide-slot';
      line.style.cssText = `font:11px var(--ax-mono);color:${sv.ok ? 'var(--ax-good)' : 'var(--ax-bad)'}`;
      line.textContent = `${sch.slots[i].name}: ${sv.label} — have ${sv.have}/${sv.need}`;
      if (sv.componentKey) {
        const c = SCHEMATIC_BY_KEY[sv.componentKey];
        if (c) {
          const nest = document.createElement('span');
          nest.style.color = 'var(--ax-muted)';
          nest.textContent = `  (made from ${c.slots.map((cs) => `${cs.qty} × ${cs.accepts.startsWith('component:') ? (SCHEMATIC_BY_KEY[cs.accepts.slice(10)]?.name ?? cs.accepts.slice(10)) : cs.accepts.startsWith('family:') ? (FAMILY_BY_KEY[cs.accepts.slice(7)]?.label ?? cs.accepts.slice(7)) : `any ${cs.accepts.slice(6)}`}`).join(', ')})`;
          line.appendChild(nest);
        }
      }
      slots.appendChild(line);
    });
    info.appendChild(slots);
    if (v.verdict === 'locked') {
      const how = document.createElement('div');
      how.style.cssText = 'margin-top:4px;color:var(--ax-warn);font:11px var(--ax-font)';
      how.textContent = SHIP_SCHEMATICS[sch.key]
        ? 'Schematic required: buy the hull design from a schematic dealer (any city or town) or reverse-engineer a hull you own (it is broken up).'
        : 'Schematic required: buy it from a schematic dealer (any city or town, Workbench → Market) or reverse-engineer a finished piece (Inventory; the piece is destroyed).';
      info.appendChild(how);
    }
    card.appendChild(info);
    const actions = document.createElement('div');
    actions.style.cssText = 'display:flex;flex-direction:column;gap:4px;flex:0 0 auto';
    const craft = document.createElement('button');
    craft.textContent = v.verdict === 'ready' ? 'Craft' : 'Open';
    craft.className = 'craft-guide-craft';
    craft.style.cssText = 'cursor:pointer;background:var(--ax-bg3);color:var(--ax-hi);border:1px solid var(--ax-line);border-radius:var(--ax-radius);padding:3px 10px;font:600 11px var(--ax-font)';
    craft.addEventListener('click', () => bus.emit('atlas:open-workbench', { tab: 'craft', key: sch.key }));
    actions.appendChild(craft);
    if (v.verdict === 'locked') {
      const shop = document.createElement('button');
      shop.textContent = 'Dealers';
      shop.style.cssText = 'cursor:pointer;background:var(--ax-bg3);color:var(--ax-hi);border:1px solid var(--ax-line);border-radius:var(--ax-radius);padding:3px 10px;font:600 11px var(--ax-font)';
      shop.addEventListener('click', () => bus.emit('atlas:open-workbench', { tab: 'market' }));
      actions.appendChild(shop);
    }
    card.appendChild(actions);
    return card;
  }
}
